import { fetchMtrDetail } from '#/api/imsMgmt/settings/mtr/fetchDetail';
import { ROUTES } from '#/config/routes';
import {
  createEmptyPurchaseOrderLine,
  mapMtrPickerRowToPurchaseOrderLine,
} from '#/modules/srmMgmt/purchaseOrder/helper/formSerialize';
import {
  getMtrPurchasableRejectReason,
  isMtrRowPurchasableInPicker,
} from '#/modules/srmMgmt/purchaseOrder/helper/validatePurchasableMtr';

export function parsePurchaseOrderCreatePrefillParams(searchParams) {
  const mtrIdRaw = searchParams.get('mtr_id')?.trim() || '';
  const orderQty = searchParams.get('order_qty')?.trim() || '';
  const mtrIds = mtrIdRaw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  return {
    mtrIds,
    orderQty,
    hasPrefill: mtrIds.length > 0 || Boolean(orderQty),
  };
}

function resolveDefaultOrderQty(orderQtyParam) {
  if (orderQtyParam === '') {
    return null;
  }
  const qty = Number(orderQtyParam);
  return Number.isFinite(qty) && qty > 0 ? qty : null;
}

/**
 * 與 mtr_id 同序解析 order_qty；單一值向後相容套全部 mtr。
 */
export function resolveOrderQtysForMtrIds(mtrIds, orderQtyParam = '') {
  const raw = orderQtyParam?.trim() || '';
  if (!raw || mtrIds.length === 0) {
    return mtrIds.map(() => null);
  }

  const parts = raw.split(',').map((part) => part.trim());

  if (parts.length === 1) {
    const sharedQty = resolveDefaultOrderQty(parts[0]);
    return mtrIds.map(() => sharedQty);
  }

  return mtrIds.map((_, index) => resolveDefaultOrderQty(parts[index] ?? ''));
}

function buildPrefillLine(row, orderQty) {
  const mapped = mapMtrPickerRowToPurchaseOrderLine({
    id: row.id,
    mtrNo: row.mtrNo,
    name: row.name,
    baseUnitPrice: row.baseUnitPrice ?? row.unitPrice ?? '',
  });

  if (!mapped) {
    return null;
  }

  if (orderQty != null) {
    return createEmptyPurchaseOrderLine({
      ...mapped,
      orderQty,
    });
  }

  return mapped;
}

/**
 * create 模式：讀 URL query → 驗證並 append 採購明細；完成後 replace 清 query。
 */
export async function prefillPurchaseOrderFromSearchParams({
  searchParams,
  getValues,
  setValue,
  navigate,
  setCustomMessages,
  markSubmitAttempted,
}) {
  const { mtrIds, orderQty, hasPrefill } = parsePurchaseOrderCreatePrefillParams(searchParams);

  if (!hasPrefill) {
    return { addedCount: 0, failedMessages: [] };
  }

  const orderQtys = resolveOrderQtysForMtrIds(mtrIds, orderQty);
  const initialLines = getValues('lines') ?? [];
  const existingIds = new Set(
    initialLines
      .map((line) => line?.mtrId)
      .filter(Boolean),
  );

  const nextLines = [...initialLines];
  const failedMessages = [];

  await Promise.all(mtrIds.map(async (mtrId, index) => {
    if (existingIds.has(mtrId)) {
      failedMessages.push(`物料 ${mtrId}：已在明細中`);
      return;
    }

    try {
      const result = await fetchMtrDetail(mtrId);
      const row = result?.data;

      if (!isMtrRowPurchasableInPicker(row)) {
        const label = row?.mtrNo || mtrId;
        failedMessages.push(`${label}：${getMtrPurchasableRejectReason(row)}`);
        return;
      }

      const line = buildPrefillLine(row, orderQtys[index]);
      if (!line) {
        failedMessages.push(`${row?.mtrNo || mtrId}：無法建立明細列`);
        return;
      }

      nextLines.push(line);
      existingIds.add(mtrId);
    } catch {
      failedMessages.push(`${mtrId}：找不到物料`);
    }
  }));

  if (nextLines.length > initialLines.length) {
    setValue('lines', nextLines, { shouldDirty: true, shouldValidate: true });
  }

  if (failedMessages.length > 0) {
    setCustomMessages(failedMessages);
    markSubmitAttempted();
  }

  navigate(ROUTES.purchaseOrderCreate, { replace: true });

  return {
    addedCount: nextLines.length - initialLines.length,
    failedMessages,
  };
}
